import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { RoasterRow } from '@/components/admin/RoasterRow';
import { RoastersEmptyState } from '@/components/admin/RoastersEmptyState';
import { type RoasterFilter, RoastersFilterTabs } from '@/components/admin/RoastersFilterTabs';
import { RoastersSearchBar } from '@/components/admin/RoastersSearchBar';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Layout } from '@/components/Layout';
import { useRunCrawl } from '@/hooks/useCrawlRuns';
import {
  type AdminRoaster,
  isRoasterComplete,
  roasterStatus,
  useRoasters,
  useUpdateRoaster,
} from '@/hooks/useRoasters';

const TOAST_DURATION_MS = 2400;

// Staff-only roaster shop inventory (issue #278; design: Roaster Inventory
// Admin). Rendering is gated by the is_admin claim in App.tsx — the real
// enforcement is the API's admin middleware, which 404s the /admin/roaster
// endpoints for non-admins.
export function RoastersPage() {
  const navigate = useNavigate();
  const { roasters, isLoading, error, refetch } = useRoasters();
  const { updateRoaster } = useUpdateRoaster();
  const { runCrawl } = useRunCrawl();

  const [filter, setFilter] = useState<RoasterFilter>('needs');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState<{ message: string; isError: boolean } | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  function showToast(message: string, isError = false) {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, isError });
    toastTimerRef.current = setTimeout(() => setToast(null), TOAST_DURATION_MS);
  }

  // `complete` is the tab count — indexed roasters plus the ones set aside as
  // having no webshop. `indexed` is the narrower number the progress line
  // reports, so marking a roaster as no-webshop never inflates it.
  const counts = useMemo(() => {
    const list = roasters ?? [];
    const complete = list.filter(isRoasterComplete).length;
    const noWebshop = list.filter((roaster) => roasterStatus(roaster) === 'no-webshop').length;
    return {
      all: list.length,
      complete,
      needs: list.length - complete,
      indexed: complete - noWebshop,
      noWebshop,
    };
  }, [roasters]);

  const visible = useMemo(() => {
    const list = roasters ?? [];
    const query = search.toLowerCase().trim();
    return list
      .filter((roaster) => {
        if (filter === 'needs') return !isRoasterComplete(roaster);
        if (filter === 'complete') return isRoasterComplete(roaster);
        return true;
      })
      .filter(
        (roaster) =>
          !query ||
          roaster.name.toLowerCase().includes(query) ||
          roaster.location.toLowerCase().includes(query),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [roasters, filter, search]);

  async function handleSave(
    id: string,
    input: { shopUrl: string; shopPlatformType: string },
  ): Promise<string | null> {
    const result = await updateRoaster(id, input);
    if (!result.ok) return result.message;
    await refetch();
    showToast(`${result.roaster.name} saved`);
    return null;
  }

  async function handleMarkNoWebshop(id: string): Promise<string | null> {
    const result = await updateRoaster(id, { hasWebshop: false });
    if (!result.ok) return result.message;
    await refetch();
    showToast(`${result.roaster.name} marked as no webshop`);
    return null;
  }

  // Queue a crawl, then jump to the runs feed where the fresh run is
  // highlighted and reports its own progress. On refusal (a roaster that turned
  // out not to be crawlable, or one already crawling) surface the server's
  // message and stay put.
  async function handleRunCrawl(roaster: AdminRoaster): Promise<void> {
    const result = await runCrawl(roaster.id);
    if (!result.ok) {
      if (result.message) showToast(result.message, true);
      return;
    }
    void navigate(`/admin/crawl-runs?triggered=${result.runId}`);
  }

  const emptyMode = search.trim() ? 'search' : filter;

  return (
    <Layout>
      <div className="mx-auto max-w-[640px] px-4 pb-10" data-testid="roasters-page">
        <div className="flex items-center gap-2 pb-1 pt-5">
          <button
            type="button"
            onClick={() => void navigate('/me')}
            aria-label="Back to account"
            data-testid="roasters-back"
            className="-ml-1.5 flex h-8 w-8 items-center justify-center rounded-lg text-wolisho-brown opacity-50 transition-opacity hover:opacity-80"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
          </button>
          <span className="text-xs font-bold uppercase tracking-wider text-wolisho-brown/60">
            Admin
          </span>
        </div>

        <h1 className="mt-1 text-2xl font-bold text-black">Roaster inventory</h1>

        {isLoading ? (
          <LoadingSpinner label="Loading roasters" testId="roasters-loading" />
        ) : error ? (
          <p className="mt-4 text-sm text-black/50" data-testid="roasters-error">
            Couldn&apos;t load the roaster list. Refresh to try again.
          </p>
        ) : (
          <>
            <p className="mb-4 mt-0.5 text-sm text-black/50" data-testid="roasters-progress">
              {counts.indexed} of {counts.all} roasters indexed
              {counts.noWebshop > 0 && ` · ${String(counts.noWebshop)} no webshop`} ·{' '}
              {counts.needs} need info
            </p>

            <div className="mb-2.5">
              <RoastersFilterTabs value={filter} onChange={setFilter} counts={counts} />
            </div>

            <div className="mb-3.5">
              <RoastersSearchBar value={search} onChange={setSearch} />
            </div>

            <div className="overflow-hidden rounded-xl bg-wolisho-card">
              {visible.length > 0 ? (
                visible.map((roaster, index) => (
                  <div key={roaster.id}>
                    {index > 0 && <div className="mx-4 h-px bg-wolisho-brown/[0.06]" />}
                    <RoasterRow
                      roaster={roaster}
                      onSave={handleSave}
                      onMarkNoWebshop={handleMarkNoWebshop}
                      onRunCrawl={handleRunCrawl}
                    />
                  </div>
                ))
              ) : (
                <RoastersEmptyState mode={emptyMode} />
              )}
            </div>
          </>
        )}
      </div>

      {toast && (
        <div
          className="pointer-events-none fixed bottom-6 left-1/2 z-50 -translate-x-1/2 whitespace-nowrap rounded-lg bg-wolisho-brown px-5 py-2.5 text-[13px] font-bold text-white"
          role={toast.isError ? 'alert' : 'status'}
          data-testid="roasters-toast"
        >
          {toast.message}
        </div>
      )}
    </Layout>
  );
}
